import {bindFormScroll} from './formScrollBridge';
// Public /f/:slug route. Fetches the published form, renders it, and
// strips all suite chrome so the form stands on its own.
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import FormRenderer from "./FormRenderer";
import { publicApi } from "./formsApi";

export default function PublicFormView({formSlug,inline=false,sourceUrl="",locale="",fieldLabels={},fieldHtml={},submitLabel,thankyouMessage,onSubmitted,submitContext,answerPatch=null}) {
  const params=useParams();
  const slug=formSlug || params.slug;
  const [form, setForm] = useState(null);
  const [state, setState] = useState("loading"); // loading|found|notfound|error

  const embedded=!inline && new URLSearchParams(window.location.search).get("embed")==="1";
  useEffect(()=>{
    if(!embedded)return;
    document.documentElement.classList.add("hsf-embedded");
    const css=document.createElement("style");
    css.textContent='html.hsf-embedded,html.hsf-embedded body{margin:0!important;min-height:0!important;background:transparent!important}html.hsf-embedded .hsf-root{min-height:0!important;padding:8px!important;background:transparent!important}html.hsf-embedded [data-testid="sales-language-bar"],html.hsf-embedded [data-testid="account-header"],html.hsf-embedded [data-testid^="hustle-voice-"]{display:none!important}';
    document.head.appendChild(css);
    const send=()=>{
      const root=document.querySelector(".hsf-root");
      if(root && window.parent!==window)window.parent.postMessage({hpBlocks:true,slug,height:Math.ceil(root.getBoundingClientRect().height)},"*");
    };
    const unbindScroll=bindFormScroll({slug});
    const observer=new ResizeObserver(send);
    const root=document.querySelector(".hsf-root");
    if(root)observer.observe(root);
    send();
    return()=>{unbindScroll();observer.disconnect();css.remove();document.documentElement.classList.remove("hsf-embedded");};
  },[embedded,slug,state]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const data = await publicApi.get(slug);
        if (cancel) return;
        setForm(data);
        setState("found");
        if (!inline && data?.name) document.title = `${data.name} · HustleShield Forms`;
      } catch (e) {
        if (cancel) return;
        setState(e.message === "404" ? "notfound" : "error");
      }
    })();
    return () => { cancel = true; };
  }, [slug,inline]);

  if(inline && state!=="found")return <div role={state==="loading"?"status":"alert"}>{state==="loading"?"Loading protected form…":"Could not load the protected form. Please refresh and try again."}</div>;
  if (state === "loading") {
    return (
      <div
        className="hsf-root"
        style={{ background: "#0b0f19", color: "#fff",
                 minHeight: "100vh", display: "grid", placeItems: "center" }}
        data-testid="public-form-loading"
      >
        Loading…
      </div>
    );
  }
  if (state === "notfound") {
    return (
      <div
        className="hsf-root"
        style={{ background: "#0b0f19", color: "#fff",
                 minHeight: "100vh", display: "grid", placeItems: "center",
                 padding: "40px 20px", textAlign: "center" }}
        data-testid="public-form-notfound"
      >
        <div>
          <div style={{ fontSize: 60, marginBottom: 12 }}>🔍</div>
          <h1 style={{ margin: "0 0 10px" }}>Form not found</h1>
          <p style={{ color: "#94a3b8" }}>
            This form is either unpublished or doesn't exist anymore.
          </p>
        </div>
      </div>
    );
  }
  if (state === "error") {
    return (
      <div
        className="hsf-root"
        style={{ background: "#0b0f19", color: "#fff",
                 minHeight: "100vh", display: "grid", placeItems: "center" }}
        data-testid="public-form-error"
      >
        Could not load this form. Please try again.
      </div>
    );
  }

  const rendered=inline?{...form,schema:{...form.schema,fields:(form.schema.fields || []).map(field=>({...field,...(fieldLabels[field.name]?{label:fieldLabels[field.name]}:{}),...(fieldHtml[field.name]?{html:fieldHtml[field.name]}:{})})),settings:{...form.schema.settings,show_title:false,show_business_name:false,...(submitLabel?{submit_label:submitLabel}:{}),...(thankyouMessage?{thankyou_message:thankyouMessage}:{})}}}:form;
  return <div className={inline?"hsf-inline":undefined}><FormRenderer key={form.id+":"+sourceUrl} form={rendered} sourceUrl={sourceUrl} screenLanguageOverride={locale} onSubmitted={onSubmitted} answerPatch={answerPatch}/></div>;
}
